import * as React from 'react';
import { useEffect, useState } from 'react';
import { Localizer } from 'services/Localizer';
//#region HCL
import { getUserData, UserData } from 'services/UserData';
//#endregion
import Spinner from 'shared/components/Spinner';
import StarRating from 'shared/components/star-rating/StarRating';
import LeftChevronSVG from 'shared/components/svg/LeftChevronSVG';
import RightChevronSVG from 'shared/components/svg/RightChevronSVG';
import InlineAlert from 'shared/edit/InlineAlert';
import useRocEventHandler from 'shared/hooks/useRocEventHandler';
import { getInitialProductDetailsState } from '../product-details/types';
import useProductReviews from './useProductReviews';
import WriteReview from './WriteReview';

export interface ReviewListingProps {
	initialProductId: string;
	ignoreSelectionChanges: 'true' | undefined;
}

/**
 * Represents a listing of reviews for a product, found as a "tab" under the product details page.
 * This is not a standard react component and relies on global state to function properly.
 * The presence of multiple review listings on the same page is erroneous.
 */
export default function ReviewListing(props: ReviewListingProps) {
	const initialState = getInitialProductDetailsState();
	const initialReviewsSummary = initialState.reviewsSummary;

	const [productId, setProductId] = useState(props.initialProductId);
	const [reviewsSummary, setReviewsSummary] = useState(initialReviewsSummary);

	//#region HCL
	const [userIsSignedIn, setUserIsSignedIn] = useState(false);

	const getUserIsSignedIn = (data: UserData) => {
		setUserIsSignedIn((data && data.summary && data.summary.isSignedIn) || false);
	};
	//#endregion

	const [loggedInUserFirstName, setLoggedInUserFirstName] = useState('');

	const {
		page,
		goToPage,
		goToNextPage,
		goToPreviousPage,
		error,
		loading,
		results,
		productReviewsError,
		hasValidationError,
		setProductReviewsError,
		setValidationHasError,
		reviewAdded,
		setReviewAdded,
		loadMoreClicked,
		isAbuseSaved,
		isSaving,
		reportAbuse,
		onLoadMoreClick,
		saveReview,
		showWriteReview,
		setShowWriteReview,
	} = useProductReviews({
		productId: productId ?? props.initialProductId,
		reviewsSummary: reviewsSummary ?? initialReviewsSummary,
	});

	useRocEventHandler(
		'product-selection-updated',
		({ product }) => {
			if (!props.ignoreSelectionChanges) {
				goToPage(1);
				setProductId(product.productId);
				setReviewsSummary(product.productDetails?.reviewsSummary);
			}
		},
		[goToPage, setProductId, setReviewsSummary],
	);

	useRocEventHandler(
		'roc-reviews-write-review',
		() => {
			setShowWriteReview(true);
		},
		[],
	);

	useEffect(() => {
		const getLoggedInUserFirstName = async (): Promise<string> => {
			try {
				const data = (await getUserData()).data;
				getUserIsSignedIn(data);
				return (data && data.summary && data.summary.firstName) || '';
			} catch (error) {
				console.error('Unable to load user data', error);
			}
			return Promise.resolve('');
		};

		getLoggedInUserFirstName().then((firstName) => {
			setLoggedInUserFirstName(firstName);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!reviewAdded) {
			return;
		}

		// after a set amount of time, hide the message
		const timer = setTimeout(() => {
			setReviewAdded(false);
		}, 5000);

		return () => {
			clearTimeout(timer);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [reviewAdded]);

	return (
		<>
			{loading && <Spinner block />}

			{productReviewsError ?? error != null ? <InlineAlert error={productReviewsError ?? error ?? null} /> : null}

			<div className="roc-pdp-reviews-listing">
				<div className="roc-pdp-reviews-listing__header">
					{!showWriteReview ? (
						<>
							{/* //#region HCL */}
							{userIsSignedIn && results && results.pagination.totalRecords === 0 && (
								<p>{Localizer('BeTheFirstToReviewThisItem')}</p>
							)}

							{!userIsSignedIn && <p>{Localizer('PleaseLoginToWriteReview')}</p>}
							{userIsSignedIn && (
								<button
									className="roc-btn roc-pdp-reviews-listing__write-review-btn"
									type="button"
									onClick={() => {
										setShowWriteReview(true);
										setProductReviewsError(null);
										setValidationHasError(false);
									}}
									data-testid="write-review-input-button"
								>
									{Localizer('WriteAReview')}
								</button>
							)}
							{/* //#endregion */}
						</>
					) : null}
				</div>

				{reviewAdded && (
					<InlineAlert
						theme="success"
						title={Localizer('Success')}
						message={Localizer('ReviewAdded')}
						error={null}
					/>
				)}

				<div className="roc-pdp-reviews-listing__body">
					<div className="roc-pdp-reviews-listing__write-review">
						{showWriteReview ? (
							<WriteReview
								hasValidationError={hasValidationError}
								isSaving={isSaving}
								saveReview={saveReview}
								setShowWriteReview={setShowWriteReview}
								productId={productId}
								loggedInUserFirstName={loggedInUserFirstName}
							/>
						) : null}
					</div>

					{results && loadMoreClicked && results.pagination.totalPages > 1 && (
						<div className="roc-pdp-reviews-listing__paginate">
							<button type="button" className="roc-btn" onClick={goToPreviousPage}>
								<LeftChevronSVG />
							</button>

							<div className="roc-pdp-reviews-listing__paginate-text">
								<input
									type="number"
									className="roc-pdp-reviews-listing__paginate-input"
									style={{ display: 'inline', width: '50px' }}
									value={page}
									onChange={(event) => goToPage(parseInt((event.target as HTMLInputElement).value))}
								/>{' '}
								of {results.pagination.totalPages}
							</div>

							<button type="button" className="roc-btn" onClick={goToNextPage}>
								<RightChevronSVG />
							</button>
						</div>
					)}

					{results && results.pagination.totalRecords > 0 && (
						<div className="roc-pdp-reviews-listing__info-bar">
							{Localizer(
								'XToYOfZ',
								((results.pagination.pageNumber - 1) * results.pagination.pageSize + 1).toString(),

								results.pagination.totalRecords <
									results.pagination.pageNumber * results.pagination.pageSize
									? results.pagination.totalRecords.toString()
									: (results.pagination.pageNumber * results.pagination.pageSize).toString(),

								results.pagination.totalRecords.toString(),
							)}{' '}
							{results.pagination.totalRecords > 1
								? Localizer('Reviews').toLowerCase()
								: Localizer('Review').toLowerCase()}
						</div>
					)}

					<div className="roc-pdp-review-list">
						{results &&
							results.results.map((review, i) => {
								return (
									<div key={i} className="roc-pdp-review-list__item">
										<div className="roc-pdp-review-list__item-header">
											<div className="roc-pdp-review-list__rating">
												<StarRating
													value={review.rating}
													name=""
													testId={`review-star-rating-${review.id}`}
													readonly
												/>
											</div>
											{(!isAbuseSaved || !isAbuseSaved[review.id]) && (
												<div className="roc-pdp-review-list__abuse-wrapper">
													<button
														type="button"
														className="roc-pdp-review-list__abuse-btn"
														onClick={() => reportAbuse(review.id)}
														disabled={isSaving}
													>
														{Localizer('ReportAbuse')}
													</button>
												</div>
											)}
											{isAbuseSaved && isAbuseSaved[review.id] && (
												<span>{Localizer('ReportSent')}</span>
											)}
										</div>
										<h2
											className="roc-pdp-review-list__title"
											data-testid={`review-listing-title-${i}`}
										>
											{review.title}
										</h2>
										<p className="roc-pdp-review-list__desc">{review.description}</p>
										<p className="roc-pdp-review-list__desc">
											<strong>{Localizer('Recommended')}</strong>:{' '}
											{review.isProductRecommended ? Localizer('Yes') : Localizer('No')}
										</p>
										<p className="roc-pdp-review-list__desc">
											{`${review.reviewerName ? review.reviewerName : Localizer('Unknown')}${
												review.location ? ', ' + review.location : ''
											} (${review.localizedReviewDate})`}
										</p>
										{isSaving && <Spinner block />}
									</div>
								);
							})}
					</div>

					{!loadMoreClicked && (
						<>
							<br />
							<button
								type="button"
								className="roc-btn"
								onClick={onLoadMoreClick}
								style={{ width: '100%' }}
							>
								{Localizer('LoadMoreReviews')}
							</button>
						</>
					)}
				</div>
			</div>
		</>
	);
}
